/**
 * Select Component
 *
 * Glass-inset styled select dropdown with theme-aware colors.
 */

import React from 'react';
import { ChevronDown } from 'lucide-react';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'children'> {
  options: SelectOption[];
  label?: string;
  error?: string;
  hint?: string;
  placeholder?: string;
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ options, label, error, hint, placeholder, className = '', id, ...props }, ref) => {
    const inputId = id || `select-${Math.random().toString(36).slice(2, 11)}`;

    const baseClasses = `
      w-full px-4 py-3 pr-10
      border rounded-xl
      bg-[rgba(0,0,0,0.03)]
      text-[var(--text-primary)]
      appearance-none cursor-pointer
      transition-all duration-150
      focus:ring-2 focus:border-transparent focus:outline-none
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const stateClasses = error
      ? 'border-[#FF3B30]/50 focus:ring-[#FF3B30]/20'
      : 'border-[var(--border-default)] focus:ring-[#5C2D91]/20 focus:border-[#5C2D91]/40';

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[var(--text-secondary)] mb-2"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={inputId}
            className={`${baseClasses} ${stateClasses} ${className}`.trim().replace(/\s+/g, ' ')}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
              >
                {option.label}
              </option>
            ))}
          </select>
          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-tertiary)] pointer-events-none" />
        </div>
        {error && (
          <p className="mt-1.5 text-sm text-[#FF3B30]">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-sm text-[var(--text-tertiary)]">{hint}</p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
